import React, { useEffect, useRef, useState } from 'react';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import { downloadByFileIdAndName, downloadByUrlAndName, downloadFile, fileUrl, urlFromFileId } from 'app/util/utils';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Paper from '@material-ui/core/Paper';
import Draggable from 'react-draggable';
import { ResizableBox } from 'react-resizable';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import { Box, CircularProgress, useMediaQuery } from '@material-ui/core';
import Popover from '@material-ui/core/Popover';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Divider from '@material-ui/core/Divider';
import { ZoomIn, ZoomOut, Download, OpenInFull, CloseFullscreen, Fullscreen, FullscreenExit, FitScreen, Pageview, KeyboardArrowUp, KeyboardArrowDown } from '@material-ui/icons';
import clsx from 'clsx';
import i18n from 'i18n';
import { clearImage } from 'app/store/fuse/previewSlice';
import { hideLoading, showLoading } from 'app/store/fuse/loadingSlice';
import PreviewSimple from './preview-simple';
import svgPanZoom from 'svg-pan-zoom';

const useStyles = makeStyles(theme => ({
	maximizeRoot: {
		padding: 0,
		width: '100%',
		height: 'calc(100% - 4rem)'
	},
	image: {
		maxWidth: 'unset',
		cursor: 'all-scroll',
		margin: 'auto'
	}
}));

function Preview(props) {
	const classes = useStyles();
	const dispatch = useDispatch();
	const newImage = useSelector(({ fuse }) => fuse.preview.image);
	const theme = useTheme();
	const [images, setImages] = useState([]);
	const [popover, setPopover] = useState({ show: false, top: 0, left: 0 });
	const svgRef = useRef({})


	const handleClose = image => {
		images.splice(images.indexOf(image), 1);
		setImages([...images]);
	};
	const handleKeyup = (e, image) => {
		if (e.key == 'Escape') {
			handleClose(image)
		}
	};
	useEffect(() => {
		if (newImage) {
			if (!images.find(i => i.id === newImage.id)) {
				const img = { ...newImage };
				img.width = 600;
				img.wrapperWidth = 628;
				img.height = 400;
				img.wrapperHight = 412;
				img.index = images.length;
				img.paperCss = {};
				img.paperCss.top = 100 + img.index * 20;
				img.paperCss.left = `calc(50% - ${314 - img.index * 20}px)`;
				img.paperCss.zIndex = Math.max(...images.map(i => i.paperCss.zIndex), 40) + 1;
				img.isFit = false;
				img.maximize = false;
				images.push(img);
				img.loading = true;
				loadImage(img)
				setImages([...images]);
			}
			dispatch(clearImage());
		}
	}, [newImage, setImages, dispatch]);


	const loadImage = (img) => {
		if (img.svg) {
			img.node = <svg>{img.svg}</svg>
			setImages(prevImages => {
				let newImages = [...prevImages];
				const index = newImages.findIndex(x => x.id == img.id);
				newImages[index] = { ...img, loading: false }
				return newImages;
			})
			return
		}
		const htmlImage = new Image();
		htmlImage.src = img.base64 || urlFromFileId(img.id);
		htmlImage.onload = () => {
			img.node = <image style={{ width: `${htmlImage.width}px`, height: `${htmlImage.height}px` }} href={img.base64 || urlFromFileId(img.id)} />
			setImages(prevImages => {
				let newImages = [...prevImages];
				const index = newImages.findIndex(x => x.id == img.id);
				newImages[index] = { ...img, loading: false }
				return newImages;
			})
		}
	}

	const maximizeImage = image => {
		image.maximize = !image.maximize;
		setImages([...images]);
	}


	const fitImage = (image, isFit) => {
		image.isFit = isFit;
		setImages([...images]);
		if (isFit) {
			svgRef.current[image.id].reset(); svgRef.current[image.id].resize(); svgRef.current[image.id].center(); svgRef.current[image.id].fit();
		} else {
			svgRef.current[image.id].reset(); svgRef.current[image.id].resize(); svgRef.current[image.id].center();
		}
	}

	const toggleMinimizeImage = (image) => {
		if (image.maximized) {
			image.maximized = false;
		}
		image.minimized = !image.minimized;
		setImages([...images]);
	}



	const inFront = image => {
		const zIndex = Math.max(...images.map(i => i.paperCss.zIndex));
		if (image.paperCss.zIndex < zIndex) {
			image.paperCss.zIndex = zIndex + 1;
			setImages([...images]);
		}
	};


	const download = image => {
		if (image.base64) {
			downloadByUrlAndName(image.base64, image.title || image.id)
		} else {
			downloadByFileIdAndName(image.id, image.title || image.id)
		}
		setPopover({ ...popover, show: false });
	};

	if (useMediaQuery(theme.breakpoints.down('sm')) && images.length)
		return (
			<PreviewSimple
				title={images[0].title}
				imageUrl={urlFromFileId(images[0].id)}
				onClose={e => setImages([])}
			/>
		);

	const dialogTitle = image => (
		<DialogTitle className="p-5 flex cursor-move h-40" disableTypography id={`image-preview${image.id}`} style={{ backgroundColor: theme.palette.primary.main, color: '#fff' }}>
			<Tooltip title={image.minimized ? 'Expand' : 'Collapse'} placement="bottom">
				<IconButton
					size="small"
					color="inherit"
					onClick={e => toggleMinimizeImage(image)}
					aria-label="close"
				>
					{image.minimized ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
				</IconButton>
			</Tooltip>
			<span className="flex-1 py-5 px-10">{image.title}</span>

			<Tooltip title={image.isFit ? 'Actual size' : 'Fit to window'} placement="bottom">
				<IconButton
					size="small"
					color="inherit"
					onClick={e => fitImage(image, !image.isFit)}
					aria-label="close"
				>
					{image.isFit ? <CloseFullscreen /> : <OpenInFull />}
				</IconButton>
			</Tooltip>
			<Tooltip title='Download' placement="bottom">
				<IconButton size="small" color="inherit" onClick={e => download(image)}>
					<Download />
				</IconButton>
			</Tooltip>
			<Tooltip title={i18n.t('fullScreenToggle')} placement="bottom">
				<IconButton
					size="small"
					color="inherit"
					onClick={e => maximizeImage(image)}
					aria-label="close"
				>
					{image.maximize ? <FullscreenExit /> : <Fullscreen />}
				</IconButton>
			</Tooltip>

			<IconButton size="small" color="inherit" onClick={e => handleClose(image)} aria-label="close">
				<CloseIcon />
			</IconButton>
		</DialogTitle>
	);


	const imagePreview = (image) => {
		setTimeout(() => {
			svgRef.current[image.id] = svgPanZoom(`#image-preview-${image.id}`, {
				controlIconsEnabled: true,
				zoomScaleSensitivity: 1,
				dblClickZoomEnabled: true,
				contain: false,
				fit: false,
				minZoom: 0.01,
				maxZoom: 8
			});
			console.log(svgRef.current[image.id]);
		}, 100);
		return <svg id={`image-preview-${image.id}`} className='h-full w-full'>
			{image.node}
		</svg>
	}

	return (
		<>
			{images.map(image =>
			(
				image.maximize ?
					(
						<Paper key={image.id} className="absolute w-full h-full z-50">
							<>
								{dialogTitle(image)}
								<DialogContent classes={{ root: classes.maximizeRoot }}>

									{image.loading ?
										<Box className='flex justify-center items-center w-full h-full'>
											<CircularProgress />
										</Box>
										: imagePreview(image)}
								</DialogContent>
							</>
						</Paper>
					)
					:
					(
						<Draggable
							bounds="parent"
							key={image.id}
							handle={`#image-preview${image.id}`}
							cancel={'[class*="MuiDialogContent-root"]'}
							onStart={() => inFront(image)}
						>
							<Paper
								className={clsx('fixed')}
								style={image.paperCss}
								onClick={() => inFront(image)}
							>
								<>
									{dialogTitle(image)}
									{!image.minimized &&

										<DialogContent classes={{ root: 'p-0' }}>
											<ResizableBox
												minConstraints={[300, 300]}
												height={image.wrapperHight}
												width={image.wrapperWidth}
												className="rc-resizable"
												style={{ margin: 'auto' }}
												onResize={() => { svgRef.current[image.id].resize(); svgRef.current[image.id].center(); image.isFit ? svgRef.current[image.id].fit() : svgRef.current[image.id].reset() }}
											>
												{image.loading ?
													<Box className='flex justify-center items-center w-full h-full'>
														<CircularProgress />
													</Box>
													: imagePreview(image)}
											</ResizableBox>
										</DialogContent>
									}
								</>
							</Paper>
						</Draggable>
					)
			)
			)}
		</>
	);
}

export default Preview;
